import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  TrendingUp, Users, Target, DollarSign, CheckCircle, 
  ArrowRight, BarChart3, Shield, Zap, Award, Phone,
  Mail, Lock, Eye, EyeOff, Building2, User,
  HeartHandshake, MapPin, Camera, CalendarCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { TurnstileWidget, turnstileEnabled } from '../components/TurnstileWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SponsorSignup = () => {
  const { user, isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  
  // Auth state
  const [authMode, setAuthMode] = useState('signin'); // 'signin' or 'signup'
  const [showPassword, setShowPassword] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [authData, setAuthData] = useState({
    email: '',
    password: '',
    name: '',
    company: '',
    phone: ''
  });

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    city: ''
  });

  const [selectedBilling, setSelectedBilling] = useState(null);

  // Handle sponsor authentication
  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);

    try {
      if (authMode === 'signin') {
        const result = await login(authData.email, authData.password);
        if (result?.requires_2fa) {
          toast.info('Check your email for verification code');
          navigate('/login', { state: { email: authData.email, from: '/sponsor-signup' } });
          return;
        }
        toast.success('Welcome back!');
      } else {
        // Register as sponsor
        if (turnstileEnabled && !turnstileToken) {
          setAuthError('Please complete the security check');
          setAuthLoading(false);
          return;
        }
        const response = await fetch(`${API}/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            email: authData.email,
            password: authData.password,
            name: authData.name,
            phone: authData.phone,
            role: 'sponsor',
            turnstile_token: turnstileToken
          })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.detail || 'Registration failed');
        }
        
        // Cookie is set by server
        toast.success('Account created! Welcome to Spinser.');
        window.location.reload();
      }
    } catch (err) {
      setAuthError(err.response?.data?.detail || err.message || 'Authentication failed');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/crm/leads`, {
        ...formData,
        source: 'landing_page',
        billing_preference: selectedBilling || 'not_selected'
      });
      setSubmitted(true);
      toast.success('Thank you! We\'ll contact you shortly.');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectPlan = (billing) => {
    setSelectedBilling(billing);
    toast.success(`${billing === 'annual' ? 'Annual' : 'Monthly'} sponsorship selected`);
    document.querySelector('[data-testid="sponsor-name"]')?.scrollIntoView({ behavior: 'smooth' });
  };

  const valueBenefits = [
    { icon: HeartHandshake, title: 'Fund Real Community Impact', desc: 'Your sponsorship helps deliver a real home improvement project to a homeowner in your community.' },
    { icon: MapPin, title: 'Receive Exclusive Local Leads', desc: 'Homeowner leads are matched by territory and are not shared with competing sponsors.' },
    { icon: Camera, title: 'Build Trust Through Content', desc: 'Each completed project can create authentic winner stories, homeowner reactions and before-and-after content.' },
    { icon: CalendarCheck, title: 'Stay Visible All Year', desc: 'Sponsor for $500 per month or make one annual payment of $5,000.' },
  ];

  const monthlyBenefits = [
    'Participate in ongoing local campaigns',
    "Exclusive homeowner leads from the sponsor's selected territory",
    'Unique sponsor referral link',
    'Sponsor dashboard access',
    'Campaign branding and recognition',
    'Lead and campaign performance data',
    'Cancel before the next billing period',
  ];

  const annualBenefits = [
    'Everything included in the monthly plan',
    'Save $1,000 annually',
    'Equivalent to two months free',
    'Maintain consistent local visibility throughout the year',
    "Priority access to campaigns in the sponsor's selected territory",
  ];

  const features = [
    { icon: Target, title: 'High-Intent Leads', desc: 'Connect with homeowners actively thinking about selling or renovating' },
    { icon: DollarSign, title: 'Lower Cost Per Lead', desc: 'Up to 70% less than traditional Facebook or Google ads' },
    { icon: Users, title: 'Property-Level Data', desc: 'Get detailed info: address, timeline, property type, intent signals' },
    { icon: Shield, title: 'Exclusive Access', desc: 'Leads from your selected territory are never shared with competing sponsors' },
    { icon: Zap, title: 'AI-Powered Scoring', desc: 'Leads are scored HOT, WARM, or COLD based on intent signals' },
    { icon: Award, title: 'Leaderboard Rankings', desc: 'Compete for "Top Sponsor" status and get featured exposure' }
  ];

  // Demo mode: sign-in wall temporarily disabled. Set to true to restore the auth gate.
  const REQUIRE_SIGNIN = true;

  // Show authentication gate if not logged in
  if (REQUIRE_SIGNIN && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900">
        <Header />
        
        <div className="pt-32 pb-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left side - Value proposition */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <div className="inline-flex items-center gap-2 bg-[#FF3B30]/20 text-[#FF3B30] font-medium px-4 py-2 rounded-full text-sm mb-6">
                  <Building2 className="w-4 h-4" />
                  Sponsor Portal
                </div>
                <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-6" style={{ fontFamily: 'Outfit' }}>
                  Access <span className="text-[#FF3B30]">Exclusive</span> Homeowner Leads
                </h1>
                <p className="text-xl text-white/70 mb-8">
                  Sign in or create a sponsor account to view our lead packages, 
                  pricing, and start connecting with high-intent homeowners in your area.
                </p>
                
                <div className="space-y-4">
                  {[
                    'Property-level data with addresses',
                    'AI-scored leads (Hot, Warm, Cold)',
                    'Exclusive access - not shared with competitors',
                    'Real homeowners actively thinking about their property'
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-3 text-white/80">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-8 flex items-center gap-4">
                  <Link to="/roi-calculator">
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-xl">
                      <BarChart3 className="w-4 h-4 mr-2" />
                      Calculate Your ROI
                    </Button>
                  </Link>
                </div>
              </motion.div>

              {/* Right side - Auth form */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-2xl p-8 shadow-2xl"
              >
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit' }}>
                    {authMode === 'signin' ? 'Welcome Back' : 'Create Sponsor Account'}
                  </h2>
                  <p className="text-neutral-500 mt-1">
                    {authMode === 'signin' 
                      ? 'Sign in to access sponsor portal' 
                      : 'Join as a sponsor to access leads'}
                  </p>
                </div>

                {authError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4 text-sm">
                    {authError}
                  </div>
                )}

                <form onSubmit={handleAuth} className="space-y-4">
                  {authMode === 'signup' && (
                    <>
                      <div>
                        <Label className="text-neutral-700">Full Name</Label>
                        <div className="relative mt-1">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                          <Input
                            type="text"
                            placeholder="John Smith"
                            value={authData.name}
                            onChange={(e) => setAuthData({...authData, name: e.target.value})}
                            className="pl-10 h-12 rounded-xl"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-neutral-700">Phone Number</Label>
                        <div className="relative mt-1">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                          <Input
                            type="tel"
                            placeholder="(416) 555-1234"
                            value={authData.phone}
                            onChange={(e) => setAuthData({...authData, phone: e.target.value})}
                            className="pl-10 h-12 rounded-xl"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <div>
                    <Label className="text-neutral-700">Email Address</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                      <Input
                        type="email"
                        placeholder="you@company.com"
                        value={authData.email}
                        onChange={(e) => setAuthData({...authData, email: e.target.value})}
                        className="pl-10 h-12 rounded-xl"
                        required
                        data-testid="sponsor-email-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-neutral-700">Password</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={authData.password}
                        onChange={(e) => setAuthData({...authData, password: e.target.value})}
                        className="pl-10 pr-10 h-12 rounded-xl"
                        required
                        data-testid="sponsor-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {authMode === 'signup' && <TurnstileWidget onVerify={setTurnstileToken} />}

                  <Button
                    type="submit"
                    disabled={authLoading}
                    className="w-full h-12 bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl font-medium"
                    data-testid="sponsor-auth-submit"
                  >
                    {authLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {authMode === 'signin' ? 'Signing in...' : 'Creating account...'}
                      </span>
                    ) : (
                      <>
                        {authMode === 'signin' ? 'Sign In to Portal' : 'Create Sponsor Account'}
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  {authMode === 'signin' ? (
                    <p className="text-neutral-600">
                      Don't have an account?{' '}
                      <button 
                        onClick={() => { setAuthMode('signup'); setAuthError(''); }}
                        className="text-[#FF3B30] font-medium hover:underline"
                      >
                        Create one
                      </button>
                    </p>
                  ) : (
                    <p className="text-neutral-600">
                      Already have an account?{' '}
                      <button 
                        onClick={() => { setAuthMode('signin'); setAuthError(''); }}
                        className="text-[#FF3B30] font-medium hover:underline"
                      >
                        Sign in
                      </button>
                    </p>
                  )}
                </div>

                <div className="mt-6 pt-6 border-t border-neutral-200">
                  <p className="text-xs text-neutral-500 text-center">
                    By signing up, you agree to our Terms of Service and Privacy Policy.
                    Sponsor accounts have access to premium features and lead data.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if user is a sponsor or admin
  const isSponsorOrAdmin = user?.role === 'admin' ||
    (user?.role === 'sponsor' && user?.approval_status === 'approved');

  // Pending sponsors see a confirmation screen instead of the portal
  if (REQUIRE_SIGNIN && user?.role === 'sponsor' && user?.approval_status !== 'approved') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900">
        <Header />
        <div className="pt-32 pb-20">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <div className="bg-white rounded-2xl p-8 shadow-xl" data-testid="sponsor-pending-approval">
              <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Building2 className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="text-2xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                Application Received
              </h2>
              <p className="text-neutral-600">
                Thanks for signing up! Your sponsor account is pending admin approval.
                You'll be notified by email once approved, and then you can access the full sponsor portal.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If logged in as homeowner, show upgrade prompt
  if (REQUIRE_SIGNIN && !isSponsorOrAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900">
        <Header />
        <div className="pt-32 pb-20">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-8 shadow-xl"
            >
              <div className="w-16 h-16 bg-[#FF3B30]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Building2 className="w-8 h-8 text-[#FF3B30]" />
              </div>
              <h2 className="text-2xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                Sponsor Portal Access Required
              </h2>
              <p className="text-neutral-600 mb-6">
                You're currently logged in as a homeowner. To access sponsor features and lead packages, 
                please contact us to upgrade your account.
              </p>
              <div className="space-y-3">
                <Button 
                  className="w-full h-12 bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl"
                  onClick={() => window.location.href = 'mailto:sponsors@tryspinser.com?subject=Upgrade to Sponsor Account'}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Contact Us to Upgrade
                </Button>
                <Link to="/dashboard" className="block">
                  <Button variant="outline" className="w-full h-12 rounded-xl">
                    Return to Dashboard
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-neutral-900 to-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="inline-flex items-center gap-2 bg-[#FF3B30]/20 text-[#FF3B30] font-medium px-4 py-2 rounded-full text-sm mb-6">
                <TrendingUp className="w-4 h-4" />
                For Real Estate Professionals
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-6" style={{ fontFamily: 'Outfit' }}>
                Get Exclusive <span className="text-[#FF3B30]">Seller Leads</span> in Your Area
              </h1>
              <p className="text-xl text-white/70 mb-8">
                Stop wasting money on cold Facebook leads. Connect with high-intent homeowners 
                who are actively engaging with their property decisions.
              </p>
              
              <div className="flex flex-wrap gap-6 text-white/80">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>Property-level data</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>Intent signals</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>Exclusive access</span>
                </div>
              </div>

              <img
                src="https://static.prod-images.emergentagent.com/jobs/7626ece3-51a5-4a98-8e4d-5a4ba64801df/images/6858589969ea0e0a4169189cb717b25f8535d7811f3bf6ec0e24566fba6f529f.jpeg"
                alt="Professional contractor ready to work"
                data-testid="sponsor-hero-image"
                className="w-full h-56 object-cover object-top rounded-2xl shadow-xl mt-8 hidden lg:block"
              />
            </motion.div>

            {/* Lead Capture Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-8 shadow-xl"
            >
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-neutral-900 mb-2">Thank You!</h3>
                  <p className="text-neutral-600 mb-6">
                    We've received your information. A member of our team will contact you within 24 hours.
                  </p>
                  <Link to="/giveaways">
                    <Button className="bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl">
                      Browse Active Giveaways
                    </Button>
                  </Link>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                    Get Started Today
                  </h2>
                  <p className="text-neutral-600 mb-6">
                    Fill out the form below and we'll show you active homeowners in your area.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="sponsor-signup-form">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Full Name</Label>
                        <Input
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="John Smith"
                          className="mt-1 rounded-xl"
                          required
                          data-testid="sponsor-name"
                        />
                      </div>
                      <div>
                        <Label>Phone</Label>
                        <Input
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="(555) 123-4567"
                          className="mt-1 rounded-xl"
                          data-testid="sponsor-phone"
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="john@remax.com"
                        className="mt-1 rounded-xl"
                        required
                        data-testid="sponsor-email"
                      />
                    </div>

                    <div>
                      <Label>Brokerage / Company</Label>
                      <Input
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        placeholder="RE/MAX Premier"
                        className="mt-1 rounded-xl"
                        data-testid="sponsor-company"
                      />
                    </div>

                    <div>
                      <Label>Your Primary City</Label>
                      <Select value={formData.city} onValueChange={(v) => setFormData({ ...formData, city: v })}>
                        <SelectTrigger className="mt-1 rounded-xl" data-testid="sponsor-city">
                          <SelectValue placeholder="Select your city" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="toronto">Toronto</SelectItem>
                          <SelectItem value="vancouver">Vancouver</SelectItem>
                          <SelectItem value="calgary">Calgary</SelectItem>
                          <SelectItem value="ottawa">Ottawa</SelectItem>
                          <SelectItem value="montreal">Montreal</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full h-12 bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl font-medium"
                      data-testid="sponsor-submit"
                    >
                      {loading ? 'Submitting...' : 'Get Started — Free Consultation'}
                    </Button>

                    <p className="text-xs text-neutral-500 text-center">
                      No credit card required. We'll reach out within 24 hours.
                    </p>
                  </form>
                </>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Spinser */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-[#FF3B30] font-semibold uppercase tracking-wider text-sm mb-2">Why Sponsors Choose Spinser</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit' }}>
              Smarter Leads, Better ROI
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 border border-neutral-200 hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 rounded-xl bg-[#FF3B30]/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-[#FF3B30]" />
                </div>
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">{feature.title}</h3>
                <p className="text-neutral-600">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-[#FF3B30] font-semibold uppercase tracking-wider text-sm mb-2">A Better Use of Your Marketing Budget</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-4" style={{ fontFamily: 'Outfit' }}>
              More Than Traditional Advertising
            </h2>
            <p className="text-neutral-600 text-lg">Your marketing budget should create something people remember.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {valueBenefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                data-testid={`value-benefit-${index}`}
                className="bg-neutral-50 rounded-2xl p-6 border border-neutral-200"
              >
                <div className="w-12 h-12 bg-[#FF3B30]/10 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-[#FF3B30]" />
                </div>
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">{benefit.title}</h3>
                <p className="text-neutral-600 text-sm leading-relaxed">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              data-testid="value-become-sponsor-btn"
              className="h-14 px-10 bg-[#FF3B30] hover:bg-[#DC2626] text-white text-lg font-medium rounded-xl"
              onClick={() => document.querySelector('[data-testid="sponsor-name"]')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Become a Sponsor
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-neutral-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-[#FF3B30] font-semibold uppercase tracking-wider text-sm mb-2">Pricing</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit' }}>
              Simple, Transparent Sponsorship
            </h2>
            <p className="text-white/80 text-lg md:text-lg font-medium mb-4">One plan. Two ways to pay.</p>
            <p className="text-white/60 max-w-2xl mx-auto">
              Join Spinser as a local sponsor, help fund real home improvement projects, and receive
              exclusive homeowner leads from your selected territory.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
            {/* Monthly card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              data-testid="pricing-monthly-card"
              className="rounded-2xl p-8 bg-white flex flex-col"
            >
              <h3 className="text-xl font-semibold mb-2 text-neutral-900">Monthly Sponsorship</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-neutral-900">$500</span>
                <span className="text-neutral-500">/month</span>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {monthlyBenefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                    <span className="text-neutral-700">{benefit}</span>
                  </li>
                ))}
              </ul>
              <Button
                data-testid="pricing-monthly-btn"
                className="w-full h-12 rounded-xl font-medium bg-neutral-900 text-white hover:bg-neutral-800"
                onClick={() => selectPlan('monthly')}
              >
                Start Monthly
              </Button>
            </motion.div>

            {/* Annual card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
              data-testid="pricing-annual-card"
              className="rounded-2xl p-8 bg-gradient-to-br from-[#FF3B30] to-[#DC2626] text-white flex flex-col"
            >
              <span className="bg-white text-[#FF3B30] text-xs font-bold px-3 py-1 rounded-full mb-4 inline-block self-start">
                BEST VALUE
              </span>
              <h3 className="text-xl font-semibold mb-2 text-white">Annual Sponsorship</h3>
              <div className="mb-2">
                <span className="text-4xl font-bold">$5,000</span>
                <span className="text-white/70">/year</span>
              </div>
              <p className="text-white/80 mb-6">One payment for 12 months of sponsorship.</p>
              <ul className="space-y-3 mb-8 flex-1">
                {annualBenefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-white shrink-0 mt-0.5" />
                    <span className="text-white/90">{benefit}</span>
                  </li>
                ))}
              </ul>
              <Button
                data-testid="pricing-annual-btn"
                className="w-full h-12 rounded-xl font-medium bg-white text-[#FF3B30] hover:bg-neutral-100"
                onClick={() => selectPlan('annual')}
              >
                Choose Annual
              </Button>
            </motion.div>
          </div>

          {/* Message below pricing */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            data-testid="pricing-message"
            className="text-center mt-14 max-w-3xl mx-auto"
          >
            <p className="text-2xl sm:text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit' }}>
              Share the project cost&mdash;not your leads.
            </p>
            <p className="text-white/70 leading-relaxed">
              Spinser combines sponsor contributions to fund meaningful home improvement projects.
              Homeowner leads are assigned exclusively based on each sponsor&apos;s selected territory
              and are never shared with competing sponsors.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-4" style={{ fontFamily: 'Outfit' }}>
            Ready to Get Better Leads?
          </h2>
          <p className="text-xl text-neutral-600 mb-8">
            Schedule a free consultation and see active homeowners in your area.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              className="h-14 px-8 bg-[#FF3B30] hover:bg-[#DC2626] text-white text-lg font-medium rounded-xl"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              Get Started Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button variant="outline" className="h-14 px-8 text-lg font-medium rounded-xl">
              <Phone className="mr-2 w-5 h-5" />
              1-800-SPINSER
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SponsorSignup;
